import React, { useEffect, useState } from "react";
import api, { describeError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Send, XCircle } from "lucide-react";

const LABEL = {
  pending: "text-gold-dark border-gold/40 bg-gold/10",
  accepted: "text-emerald-700 border-emerald-200 bg-emerald-50",
  expired: "text-muted-foreground border-border bg-secondary",
  revoked: "text-muted-foreground border-border bg-secondary",
};

export const ClientInviteDialog = ({ client, open, onOpenChange }) => {
  const [email, setEmail] = useState("");
  const [invites, setInvites] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${client.id}/invites`);
      setInvites(data);
    } catch { setInvites([]); }
  };

  useEffect(() => {
    if (open) { setEmail(client.email || ""); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client.id]);

  const send = async () => {
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      toast.error("Enter a valid email address for the client");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/invite`, { email: value });
      toast.success(data.email_sent
        ? `Invitation sent to ${value}`
        : "Invitation created, but the email could not be delivered");
      load();
    } catch (e) {
      toast.error(describeError(e, "Could not send the invitation."));
    } finally {
      setSending(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.post(`/client-invites/${id}/revoke`);
      toast.success("Invitation revoked");
      load();
    } catch (e) {
      toast.error(describeError(e, "Could not revoke the invitation."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="client-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-navy">Invite to client portal</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {client.name} will get a secure link to set a password and follow only their own matter.
          Your notes and other clients stay private.
        </p>
        <div className="space-y-2">
          <Label htmlFor="invite-email">Client email</Label>
          <Input id="invite-email" type="email" value={email} data-testid="invite-email-input"
                 onChange={(e) => setEmail(e.target.value)} placeholder="client@example.com" />
        </div>

        {!!invites?.length && (
          <div className="space-y-2" data-testid="invite-history">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Invitations
            </p>
            {invites.map((i) => (
              <div key={i.id} data-testid={`invite-row-${i.id}`}
                   className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
                <span className="min-w-0">
                  <span className="block truncate text-sm text-navy">{i.email}</span>
                  <span className="text-[11px] text-muted-foreground">
                    Expires {new Date(i.expires_at).toLocaleDateString("en-IN",
                      { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${LABEL[i.status]}`}>
                    {i.status}
                  </span>
                  {i.status === "pending" && (
                    <button type="button" onClick={() => revoke(i.id)}
                            data-testid={`invite-revoke-${i.id}`}
                            className="text-muted-foreground transition-colors hover:text-destructive">
                      <XCircle className="h-4 w-4" />
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button onClick={send} disabled={sending} data-testid="invite-send-btn"
                  className="bg-navy text-white hover:bg-navy-light">
            <Send className="mr-1.5 h-4 w-4" />
            {sending ? "Sending…" : "Send invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
